import { FormEvent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useAuth0 } from '@auth0/auth0-react';
import _ from 'lodash';
import { Icon, Stack, TextField } from '@fluentui/react';
import useDebounce from '../../components/hooks/useDebounce';
import {
  default_page_size,
  stackTokens15,
  textFieldStyles300,
} from '../../utils';
import { GET_ARCHITECTS, IArchitect } from '../../utils/Architect';
import { useAppDispatch } from '../../redux/hooks';
import { SeverityLevel, throwError } from '../../redux/error/errorSlice';
import { toastError } from '../../utils/toast';
import PageHeader from '../../components/parts/PageHeader';
import ArchitectsOverview from './components/ArchitectsOverview';

interface IPagedArchitect {
  architects: IArchitect[];
  skip: number;
  take: number;
  filter: string;
  initialLoad: boolean;
  showLoadMore: boolean;
  lazyLoading: boolean;
  isFiltered: boolean;
  isSortedAsc: boolean;
  sortedField: string;
}

const Architects = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth0();
  const [pagedState, setPagedState] = useState<IPagedArchitect>({
    architects: [],
    skip: 0,
    take: default_page_size,
    filter: '',
    showLoadMore: true,
    lazyLoading: true,
    isFiltered: false,
    initialLoad: true,
    isSortedAsc: true,
    sortedField: 'last_name',
  });

  const [filter, setFilter] = useState('');

  const debouncedValue = useDebounce(filter, 500);

  useEffect(() => {
    setPagedState(prevState => ({ ...prevState, architects: [], skip: 0 }));
  }, [filter]);

  const getOrderBy = () => {
    const orderByObject: any = {}; // deliberately kept any as type to index by string
    orderByObject[pagedState.sortedField] = pagedState.isSortedAsc
      ? 'asc'
      : 'desc';
    return orderByObject;
  };

  const getFilters = (filterString: string) => {
    const filterArray = filterString.split(' ');

    const filterObject: any = { AND: [] };
    for (let i = 0; i < filterArray.length; i++) {
      const filterValue: any = {
        OR: [
          { first_name: { contains: filterArray[i] } },
          { last_name: { contains: filterArray[i] } },
          { company: { contains: filterArray[i] } },
          { city: { contains: filterArray[i] } },
        ],
      };

      filterObject.AND.push(filterValue);
    }

    return filterObject;
  };

  const {
    data: dataArchitects,
    loading,
    error,
    fetchMore,
    refetch,
  } = useQuery(GET_ARCHITECTS, {
    // Geen client.cache.reset() meer bij zoeken/sorteren: elke filter + sortering heeft een eigen
    // cache-ingang (skipTakePagination in App.tsx). Eerst tonen wat in de cache zit en altijd
    // verversen bij de server; daarna cache-first, zodat "meer laden" geen extra verversing uitlokt.
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    // /notifyOnNetworkStatusChange: true,
    variables: {
      filter: getFilters(debouncedValue),
      take: pagedState.take,
      skip: 0,
      orderBy: getOrderBy(),
    },
    onError: error => {
      toastError(error.message);
    },
    // fetchPolicy: 'no-cache',
  });

  const onSearchValueChange = (
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string | undefined,
  ) => {
    const filterString = newValue ? newValue.toLowerCase() : '';
    setFilter(filterString);
  };

  const setSorting = (isSortedAsc: boolean, sortedField: string) => {
    clearArchitects();
    setPagedState(prevState => ({
      ...prevState,
      isSortedAsc,
      sortedField,
    }));
  };

  const clearArchitects = () => {
    setPagedState(prevState => ({
      ...prevState,
      architects: [],
      skip: 0,
    }));
  };

  const fetchMoreArchitects = () => {
    // verder vanaf wat er getoond wordt (zoals bij leads/klanten)
    const currentCount = dataArchitects?.findManyArchitects?.length || 0;
    // Load more guard

    fetchMore({
      variables: {
        filter: getFilters(debouncedValue), // inUse == active?
        take: pagedState.take,
        skip: currentCount,
        orderBy: getOrderBy(),
      },
    });

    setPagedState(prevState => ({
      ...prevState,
      skip: currentCount,
    }));
  };

  if (!isAuthenticated) return <p>Verboden</p>;
  if (error) return <p>Oeps, er ging iets mis...</p>;

  return (
    <Stack tokens={stackTokens15}>
      <div style={{ padding: '25px' }}>
        <PageHeader
          title="Architecten"
          iconName="EditContact"
          loading={loading}
        />

        <TextField
          label='Zoeken...'
          value={filter}
          styles={textFieldStyles300}
          onChange={onSearchValueChange}
        />
      </div>

      <ArchitectsOverview
        refetch={refetch}
        architects={dataArchitects ? dataArchitects.findManyArchitects : []}
        loading={loading}
        setSorting={setSorting}
        isSortedAsc={pagedState.isSortedAsc}
        sortedField={pagedState.sortedField}
        showLoadMore={pagedState.showLoadMore}
        loadMore
        loadMoreCallback={fetchMoreArchitects}
        lazyLoading={pagedState.lazyLoading}
      />
    </Stack>
  );
};

export default Architects;
